import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import QRCode from "qrcode";
import JSZip from "jszip";

// @mui material components
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { chuVuonApi } from "api/chuVuon";
import { vuonApi } from "api/vuon";
import { useAuth } from "context/AuthContext";
import DauTrang, { NhanDau } from "components/DauTrang";
import { oTieuDeCot, AN_DUOI_MD, AN_DUOI_SM, chuXuongDong } from "lib/kieuBangDuLieu";
import {
  useDialogFullManHinh,
  oDialogContent,
  oDialogActions,
  oDialogContentFormDai,
  oDialogActionsFormDai,
  oDialogPaperFormDai,
  oDialogFormFlex,
} from "lib/dungDialogFullManHinh";
import { xuatExcelChuVuonVuon } from "lib/xuatExcelChuVuonVuon";
import QrCodeImage from "components/QrCodeImage";
import TaiXuongQr from "components/TaiXuongQr";
import FormChuVuon, { RONG_CHU_VUON } from "components/FormChuVuon";
import FormVuon, { RONG_VUON } from "components/FormVuon";
import { kiemTraThongTinVuon } from "lib/thongTinVuon";
import { cmSangPx, layKichThuocQrMacDinhCm, luuKichThuocQrMacDinhCm } from "lib/caiDat";
import { taoAnhQrKemMa } from "lib/qrAnh";
import { kiemTraThongTinCaNhanChuVuon } from "lib/thongTinCaNhanChuVuon";
import { chuanHoaMaTimKiem } from "lib/chuanHoaMaTimKiem";
import { hienThiCccd, soCccdDayDuTuHoSo } from "lib/cccdBaoMat";
import {
  taiThePng,
  taiThePdf,
  taiZipThePng,
  taiPdfNhieuThe,
  inHangLoatThe,
} from "lib/theKhachHangChuVuon";
import useTour from "hooks/useTour";
import buocQuanLyChuVuonVuon from "tours/quanLyChuVuonVuon";

// Dòng gom thông tin phụ dưới tên, chỉ hiện khi các cột phụ đã bị ẩn.
function ThongTinPhu({ phan }) {
  const con = phan.filter(Boolean);
  if (con.length === 0) return null;
  return (
    <MDTypography
      variant="caption"
      color="text"
      display={{ xs: "block", md: "none" }}
      mt={0.25}
      lineHeight={1.4}
      sx={chuXuongDong}
    >
      {con.join(" · ")}
    </MDTypography>
  );
}

ThongTinPhu.propTypes = {
  phan: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function QuanLyChuVuonVuon() {
  const { vaiTro } = useAuth();
  const laAdmin = vaiTro === "admin";
  const fullManHinh = useDialogFullManHinh();
  const [chuVuonList, setChuVuonList] = useState([]);
  const [vuonList, setVuonList] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState("");
  const [thongBao, setThongBao] = useState("");

  const [timChuVuon, setTimChuVuon] = useState("");
  const [timVuon, setTimVuon] = useState("");

  const [tab, setTab] = useState(0);

  const [modalChuVuon, setModalChuVuon] = useState(null);
  const [modalVuon, setModalVuon] = useState(null);
  const [diaChiVuonGiongChu, setDiaChiVuonGiongChu] = useState(true);
  const [xemQrMaChuVuon, setXemQrMaChuVuon] = useState(null);
  const [dangLuu, setDangLuu] = useState(false);

  const [daChonCV, setDaChonCV] = useState(() => new Set());
  const [kichThuocHangLoatCm, setKichThuocHangLoatCm] = useState(layKichThuocQrMacDinhCm);
  const [dangXuLyHangLoat, setDangXuLyHangLoat] = useState(false);
  const [daCopyId, setDaCopyId] = useState(null);
  const [dangXuatExcel, setDangXuatExcel] = useState(false);

  async function saoChepMaChuVuon(maChuVuon, id) {
    try {
      await navigator.clipboard.writeText(maChuVuon);
      setDaCopyId(id);
      setTimeout(() => setDaCopyId((cur) => (cur === id ? null : cur)), 1500);
    } catch {
      setLoi("Không sao chép được — trình duyệt chặn quyền clipboard.");
    }
  }

  async function taiLai() {
    // Realtime onSnapshot cập nhật danh sách — giữ stub để chỗ lưu/sửa
    // sau mutation không phải đổi chữ ký gọi.
  }

  useEffect(() => {
    setDangTai(true);
    let cvXong = false;
    let vXong = false;
    const danhDauXong = () => {
      if (cvXong && vXong) setDangTai(false);
    };
    const huyCv = chuVuonApi.langNgheDanhSach(
      (cv) => {
        setChuVuonList(cv);
        cvXong = true;
        danhDauXong();
      },
      (err) => {
        setLoi(err.message);
        cvXong = true;
        danhDauXong();
      }
    );
    const huyV = vuonApi.langNgheDanhSach(
      (v) => {
        setVuonList(v);
        vXong = true;
        danhDauXong();
      },
      (err) => {
        setLoi(err.message);
        vXong = true;
        danhDauXong();
      }
    );
    return () => {
      huyCv();
      huyV();
    };
  }, []);

  const tenChuVuonTheoId = useMemo(
    () => Object.fromEntries(chuVuonList.map((cv) => [cv.id, cv.hoTen])),
    [chuVuonList]
  );

  const chuVuonDangChon = useMemo(
    () => chuVuonList.find((cv) => cv.id === modalVuon?.maChuVuon) ?? null,
    [chuVuonList, modalVuon?.maChuVuon]
  );

  const chuVuonHienThi = useMemo(() => {
    const q = timChuVuon.trim().toLowerCase();
    if (!q) return chuVuonList;
    const qMa = chuanHoaMaTimKiem(timChuVuon);
    return chuVuonList.filter((cv) => {
      const theoChu = [cv.hoTen, hienThiCccd(cv, vaiTro), cv.cccd, cv.cccdDayDu, cv.sdt].some((v) =>
        v?.toLowerCase().includes(q)
      );
      const theoMa = qMa && chuanHoaMaTimKiem(cv.maChuVuon).includes(qMa);
      return theoChu || theoMa;
    });
  }, [chuVuonList, timChuVuon, vaiTro]);

  const vuonHienThi = useMemo(() => {
    const q = timVuon.trim().toLowerCase();
    if (!q) return vuonList;
    return vuonList.filter((v) =>
      [v.tenVuon, tenChuVuonTheoId[v.maChuVuon], v.tinh, v.xa].some((x) =>
        x?.toLowerCase().includes(q)
      )
    );
  }, [vuonList, timVuon, tenChuVuonTheoId]);

  async function xuatExcel() {
    setLoi("");
    setDangXuatExcel(true);
    try {
      // Xuất đúng danh sách đang lọc trên UI (không tìm = toàn bộ).
      await xuatExcelChuVuonVuon({
        danhSachChuVuon: chuVuonHienThi,
        danhSachVuon: vuonHienThi,
        tenChuVuonTheoId,
      });
    } catch (err) {
      setLoi(err.message || "Không xuất được file Excel.");
    } finally {
      setDangXuatExcel(false);
    }
  }

  const cvCoTheChonHangLoat = useMemo(
    () => chuVuonHienThi.filter((cv) => cv.maChuVuon),
    [chuVuonHienThi]
  );

  function chonMotCV(id) {
    setDaChonCV((s) => {
      const moi = new Set(s);
      if (moi.has(id)) moi.delete(id);
      else moi.add(id);
      return moi;
    });
  }

  function chonTatCaCV() {
    setDaChonCV((s) =>
      s.size === cvCoTheChonHangLoat.length
        ? new Set()
        : new Set(cvCoTheChonHangLoat.map((cv) => cv.id))
    );
  }

  const cvDaChon = cvCoTheChonHangLoat.filter((cv) => daChonCV.has(cv.id));

  // Cùng dùng chung "kích thước QR mặc định" (cm) với nút tải 1 mã (TaiXuongQr).
  async function taiXuongHangLoatCV() {
    setDangXuLyHangLoat(true);
    setLoi("");
    try {
      const cm = Number(kichThuocHangLoatCm) || 4;
      luuKichThuocQrMacDinhCm(cm);
      const px = cmSangPx(cm);
      const zip = new JSZip();
      for (const cv of cvDaChon) {
        const dataUrl = await taoAnhQrKemMa(cv.maChuVuon, px);
        zip.file(`${cv.maChuVuon}.png`, dataUrl.split(",")[1], { base64: true });
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr-chu-vuon-${new Date().toISOString().slice(0, 10)}.zip`;
      // Phải gắn vào DOM rồi mới click() để trình duyệt chắc chắn nhận thao
      // tác tải xuống; revoke URL trễ 1 nhịp vì click() kích hoạt tải xuống
      // bất đồng bộ — revoke ngay lập tức có thể huỷ blob trước khi trình
      // duyệt kịp đọc, làm file tải về rỗng/hỏng.
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXuLyHangLoat(false);
    }
  }

  async function inHangLoatCV() {
    setDangXuLyHangLoat(true);
    setLoi("");
    try {
      const cuaSo = window.open("", "_blank");
      if (!cuaSo) {
        setLoi("Trình duyệt đã chặn cửa sổ in — cho phép popup cho trang này rồi thử lại.");
        return;
      }
      const cm = Number(kichThuocHangLoatCm) || 4;
      luuKichThuocQrMacDinhCm(cm);
      const px = cmSangPx(cm);
      const items = await Promise.all(
        cvDaChon.map(async (cv) => ({
          ma: cv.maChuVuon,
          ten: cv.hoTen,
          src: await QRCode.toDataURL(cv.maChuVuon, { width: px, margin: 1 }),
        }))
      );
      const html = `<!DOCTYPE html><html lang="vi"><head><meta charset="utf-8" />
<title>In QR chu vuon hang loat</title>
<style>
  body { font-family: Arial, sans-serif; margin: 1cm; }
  .luoi { display: flex; flex-wrap: wrap; gap: 0.5cm; }
  .nhan { width: ${cm}cm; text-align: center; page-break-inside: avoid; }
  .nhan img { width: ${cm}cm; height: ${cm}cm; display: block; }
  .nhan span { display: block; font-weight: bold; margin-top: 2mm; font-size: 3.5mm; }
  .nhan small { display: block; font-size: 3mm; }
</style>
</head><body>
<div class="luoi">
${items
  .map(
    (i) =>
      `<div class="nhan"><img src="${i.src}" alt="${i.ma}" /><span>${i.ma}</span><small>${i.ten}</small></div>`
  )
  .join("")}
</div>
<script>window.onload = () => window.print();</script>
</body></html>`;
      cuaSo.document.write(html);
      cuaSo.document.close();
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXuLyHangLoat(false);
    }
  }

  async function taiZipTheKhachHang() {
    setDangXuLyHangLoat(true);
    setLoi("");
    try {
      await taiZipThePng(cvDaChon);
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXuLyHangLoat(false);
    }
  }

  async function taiPdfTheKhachHangHangLoat() {
    setDangXuLyHangLoat(true);
    setLoi("");
    try {
      await taiPdfNhieuThe(cvDaChon);
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXuLyHangLoat(false);
    }
  }

  async function inTheKhachHangHangLoat() {
    setDangXuLyHangLoat(true);
    setLoi("");
    try {
      await inHangLoatThe(cvDaChon);
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXuLyHangLoat(false);
    }
  }

  async function taiThePngMotCV() {
    if (!xemQrMaChuVuon) return;
    setDangXuLyHangLoat(true);
    setLoi("");
    try {
      await taiThePng(xemQrMaChuVuon);
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXuLyHangLoat(false);
    }
  }

  async function taiThePdfMotCV() {
    if (!xemQrMaChuVuon) return;
    setDangXuLyHangLoat(true);
    setLoi("");
    try {
      await taiThePdf(xemQrMaChuVuon);
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXuLyHangLoat(false);
    }
  }

  async function luuChuVuon(e) {
    e.preventDefault();
    setDangLuu(true);
    setLoi("");
    setThongBao("");
    try {
      const { id, ...data } = modalChuVuon;
      const daKiem = kiemTraThongTinCaNhanChuVuon(data);
      if (id) {
        await chuVuonApi.capNhat(id, daKiem);
      } else {
        await chuVuonApi.taoMoi(daKiem);
      }
      setModalChuVuon(null);
      await taiLai();
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuu(false);
    }
  }

  async function luuVuon(e) {
    e.preventDefault();
    setDangLuu(true);
    setLoi("");
    setThongBao("");
    try {
      const { id, ...data } = modalVuon;
      const daKiem = kiemTraThongTinVuon(data);
      const payload = {
        ...daKiem,
        dienTichHa: daKiem.dienTichHa,
        namBatDauTrong: data.namBatDauTrong ? Number(data.namBatDauTrong) : null,
        soCay: data.soCay ? Number(data.soCay) : null,
      };
      if (id) {
        await vuonApi.capNhat(id, payload);
      } else {
        await vuonApi.taoMoi(payload);
      }
      setModalVuon(null);
      await taiLai();
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuu(false);
    }
  }

  function diaChiTheoChuVuon(maChuVuon) {
    const cv = chuVuonList.find((c) => c.id === maChuVuon);
    return {
      tinh: cv?.tinh ?? "",
      xa: cv?.xa ?? "",
      thon: cv?.thon ?? "",
      diaChiChiTiet: cv?.diaChi ?? "",
    };
  }

  function chonChuSoHuu(maChuVuon) {
    setModalVuon({
      ...modalVuon,
      maChuVuon,
      ...(diaChiVuonGiongChu ? diaChiTheoChuVuon(maChuVuon) : {}),
    });
  }

  useTour("quan-ly-chu-vuon-vuon", buocQuanLyChuVuonVuon());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Quản lý"
          tieuDe="Chủ vườn / Vườn"
          moTa="Hồ sơ chủ vườn và vườn trồng — thông tin định danh, liên hệ, địa giới, giống bơ."
        >
          <MDBox display="flex" flexWrap="wrap" gap={1} justifyContent="flex-end">
            {laAdmin && (
              <MDButton
                id="tour-quan-ly-chu-vuon-vuon-xuat-excel"
                variant="outlined"
                color="dark"
                size="small"
                onClick={xuatExcel}
                disabled={
                  dangXuatExcel || dangTai || (chuVuonList.length === 0 && vuonList.length === 0)
                }
              >
                <Icon sx={{ mr: 0.75 }}>download</Icon>
                {dangXuatExcel ? "Đang xuất..." : "Xuất Excel"}
              </MDButton>
            )}
            {laAdmin &&
              (tab === 0 ? (
                <MDButton
                  id="tour-quan-ly-chu-vuon-vuon-nut-them"
                  variant="gradient"
                  color="info"
                  onClick={() => setModalChuVuon({ ...RONG_CHU_VUON })}
                >
                  <Icon sx={{ mr: 0.75 }}>add</Icon>
                  Thêm chủ vườn
                </MDButton>
              ) : (
                <MDButton
                  id="tour-quan-ly-chu-vuon-vuon-nut-them"
                  variant="gradient"
                  color="info"
                  onClick={() => {
                    setModalVuon({ ...RONG_VUON });
                    setDiaChiVuonGiongChu(true);
                  }}
                  disabled={chuVuonList.length === 0}
                >
                  <Icon sx={{ mr: 0.75 }}>add</Icon>
                  Thêm vườn
                </MDButton>
              ))}
          </MDBox>
        </DauTrang>

        {loi && (
          <MDBox mb={2}>
            <MDAlert color="error">{loi}</MDAlert>
          </MDBox>
        )}
        {thongBao && (
          <MDBox mb={2}>
            <MDAlert color="success">{thongBao}</MDAlert>
          </MDBox>
        )}

        <AppBar id="tour-quan-ly-chu-vuon-vuon-tabs" position="static" sx={{ mb: 3 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab
              label="Chủ vườn"
              icon={
                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                  person
                </Icon>
              }
            />
            <Tab
              label="Vườn"
              icon={
                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                  yard
                </Icon>
              }
            />
          </Tabs>
        </AppBar>

        {tab === 0 && (
          <>
            {laAdmin && daChonCV.size > 0 && (
              <Card sx={{ mb: 2, p: 2 }}>
                <MDBox display="flex" alignItems="flex-end" flexWrap="wrap" gap={2}>
                  <MDBox sx={{ alignSelf: "center" }}>
                    <MDTypography variant="overline" color="text" lineHeight={1} display="block">
                      Đang chọn
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="medium">
                      {daChonCV.size} chủ vườn
                    </MDTypography>
                  </MDBox>
                  <MDBox width="10rem">
                    <MDInput
                      type="number"
                      label="Kích thước QR (cm)"
                      value={kichThuocHangLoatCm}
                      onChange={(e) => setKichThuocHangLoatCm(e.target.value)}
                      inputProps={{ min: 1, max: 20, step: 0.5 }}
                      fullWidth
                    />
                  </MDBox>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    onClick={taiXuongHangLoatCV}
                    disabled={dangXuLyHangLoat}
                  >
                    {dangXuLyHangLoat ? "Đang xử lý..." : "Tải xuống hàng loạt (.zip)"}
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    onClick={inHangLoatCV}
                    disabled={dangXuLyHangLoat}
                  >
                    {dangXuLyHangLoat ? "Đang xử lý..." : "In hàng loạt"}
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={inTheKhachHangHangLoat}
                    disabled={dangXuLyHangLoat}
                  >
                    {dangXuLyHangLoat ? "Đang xử lý..." : "In thẻ khách hàng"}
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={taiZipTheKhachHang}
                    disabled={dangXuLyHangLoat}
                  >
                    {dangXuLyHangLoat ? "Đang xử lý..." : "Tải ZIP thẻ (PNG)"}
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={taiPdfTheKhachHangHangLoat}
                    disabled={dangXuLyHangLoat}
                  >
                    {dangXuLyHangLoat ? "Đang xử lý..." : "Tải PDF thẻ"}
                  </MDButton>
                  <MDButton
                    variant="text"
                    color="secondary"
                    size="small"
                    onClick={() => setDaChonCV(new Set())}
                  >
                    Bỏ chọn
                  </MDButton>
                </MDBox>
              </Card>
            )}

            <Card sx={{ mb: 4 }}>
              <MDBox
                px={2.5}
                py={2}
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <MDInput
                  id="tour-quan-ly-chu-vuon-vuon-tim"
                  label="Tìm chủ vườn"
                  placeholder="Tên, CCCD, SĐT hoặc mã"
                  value={timChuVuon}
                  onChange={(e) => setTimChuVuon(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon fontSize="small">search</Icon>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: { xs: "100%", sm: "22rem" } }}
                />
                <MDTypography variant="caption" color="text">
                  {chuVuonHienThi.length} chủ vườn
                </MDTypography>
              </MDBox>

              {dangTai ? (
                <MDBox px={2.5} pb={3}>
                  <MDTypography variant="button" color="text">
                    Đang tải...
                  </MDTypography>
                </MDBox>
              ) : chuVuonHienThi.length === 0 ? (
                <MDBox px={2.5} pb={4} pt={1} textAlign="center">
                  <Icon sx={({ palette }) => ({ color: palette.grey[400], fontSize: "2.5rem" })}>
                    person
                  </Icon>
                  <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                    {chuVuonList.length === 0
                      ? "Chưa có chủ vườn nào."
                      : "Không tìm thấy chủ vườn phù hợp."}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                    {chuVuonList.length === 0
                      ? "Bấm “Thêm chủ vườn” để tạo hồ sơ đầu tiên."
                      : "Thử bớt từ khoá hoặc tìm theo CCCD."}
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox sx={{ overflowX: "auto" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {laAdmin && (
                          <TableCell padding="checkbox" sx={[oTieuDeCot, AN_DUOI_SM]}>
                            <Checkbox
                              checked={
                                cvCoTheChonHangLoat.length > 0 &&
                                daChonCV.size === cvCoTheChonHangLoat.length
                              }
                              indeterminate={
                                daChonCV.size > 0 && daChonCV.size < cvCoTheChonHangLoat.length
                              }
                              onChange={chonTatCaCV}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Mã chủ vườn</TableCell>
                        <TableCell sx={oTieuDeCot}>Họ tên</TableCell>
                        <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>CCCD</TableCell>
                        <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>SĐT</TableCell>
                        <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Ngân hàng</TableCell>
                        <TableCell align="right" sx={oTieuDeCot}>
                          Thao tác
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chuVuonHienThi.map((cv) => (
                        <TableRow
                          key={cv.id}
                          sx={({ palette }) => ({
                            transition: "background-color 160ms ease",
                            "&:hover": { backgroundColor: palette.grey[100] },
                          })}
                        >
                          {laAdmin && (
                            <TableCell padding="checkbox" sx={AN_DUOI_SM}>
                              {cv.maChuVuon && (
                                <Checkbox
                                  checked={daChonCV.has(cv.id)}
                                  onChange={() => chonMotCV(cv.id)}
                                />
                              )}
                            </TableCell>
                          )}
                          <TableCell sx={AN_DUOI_MD}>
                            {cv.maChuVuon ? (
                              <MDBox display="flex" alignItems="center" gap={0.25}>
                                <MDTypography
                                  component={Link}
                                  to={`/chu-vuon/${cv.id}`}
                                  variant="button"
                                  fontWeight="medium"
                                  color="info"
                                  sx={chuXuongDong}
                                >
                                  {cv.maChuVuon}
                                </MDTypography>
                                <IconButton
                                  size="small"
                                  color={daCopyId === cv.id ? "success" : "secondary"}
                                  onClick={() => saoChepMaChuVuon(cv.maChuVuon, cv.id)}
                                  sx={{ p: 0.25 }}
                                >
                                  <Icon fontSize="small" sx={{ fontSize: "0.95rem !important" }}>
                                    {daCopyId === cv.id ? "check" : "content_copy"}
                                  </Icon>
                                </IconButton>
                              </MDBox>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>
                            <MDTypography
                              variant="button"
                              fontWeight="medium"
                              display="block"
                              sx={chuXuongDong}
                            >
                              {cv.hoTen}
                            </MDTypography>
                            <ThongTinPhu
                              phan={[cv.maChuVuon, hienThiCccd(cv, vaiTro), cv.sdt, cv.tenNganHang]}
                            />
                          </TableCell>
                          <TableCell sx={AN_DUOI_MD}>{hienThiCccd(cv, vaiTro)}</TableCell>
                          <TableCell sx={AN_DUOI_MD}>{cv.sdt}</TableCell>
                          <TableCell sx={AN_DUOI_MD}>{cv.tenNganHang ?? "—"}</TableCell>
                          <TableCell align="right">
                            <MDBox
                              display="flex"
                              flexWrap="wrap"
                              justifyContent="flex-end"
                              gap={0.5}
                            >
                              <MDButton
                                component={Link}
                                to={`/chu-vuon/${cv.id}`}
                                variant="text"
                                color="dark"
                                size="small"
                              >
                                Xem
                              </MDButton>
                              {cv.maChuVuon && (
                                <MDButton
                                  variant="text"
                                  color="dark"
                                  size="small"
                                  onClick={() => setXemQrMaChuVuon(cv)}
                                >
                                  QR mã CV
                                </MDButton>
                              )}
                              <MDButton
                                variant="text"
                                color="info"
                                size="small"
                                onClick={() => setModalChuVuon(cv)}
                              >
                                Sửa
                              </MDButton>
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </MDBox>
              )}
            </Card>
          </>
        )}

        {tab === 1 && (
          <Card>
            <MDBox
              px={2.5}
              py={2}
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <MDInput
                label="Tìm vườn"
                placeholder="Tên vườn, chủ vườn, địa giới"
                value={timVuon}
                onChange={(e) => setTimVuon(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon fontSize="small">search</Icon>
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: "100%", sm: "22rem" } }}
              />
              <MDTypography variant="caption" color="text">
                {vuonHienThi.length} vườn
              </MDTypography>
            </MDBox>

            {vuonHienThi.length === 0 ? (
              <MDBox px={2.5} pb={4} pt={1} textAlign="center">
                <Icon sx={({ palette }) => ({ color: palette.grey[400], fontSize: "2.5rem" })}>
                  yard
                </Icon>
                <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                  {vuonList.length === 0 ? "Chưa có vườn nào." : "Không tìm thấy vườn phù hợp."}
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                  {vuonList.length === 0
                    ? "Cần có chủ vườn trước, rồi bấm “Thêm vườn” để gán vườn cho họ."
                    : "Thử bớt từ khoá hoặc tìm theo tên xã."}
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={oTieuDeCot}>Tên vườn</TableCell>
                      <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Chủ vườn</TableCell>
                      <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Địa giới</TableCell>
                      <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Giống bơ</TableCell>
                      <TableCell align="right" sx={oTieuDeCot}>
                        Thao tác
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vuonHienThi.map((v) => (
                      <TableRow
                        key={v.id}
                        sx={({ palette }) => ({
                          transition: "background-color 160ms ease",
                          "&:hover": { backgroundColor: palette.grey[100] },
                        })}
                      >
                        <TableCell>
                          <MDTypography
                            variant="button"
                            fontWeight="medium"
                            display="block"
                            sx={chuXuongDong}
                          >
                            {v.tenVuon}
                          </MDTypography>
                          <ThongTinPhu
                            phan={[
                              tenChuVuonTheoId[v.maChuVuon],
                              [v.xa, v.tinh].filter(Boolean).join(", "),
                              v.giongBo?.join(", "),
                            ]}
                          />
                        </TableCell>
                        <TableCell sx={[AN_DUOI_MD, chuXuongDong]}>
                          {tenChuVuonTheoId[v.maChuVuon] ?? "—"}
                        </TableCell>
                        <TableCell sx={AN_DUOI_MD}>
                          {v.xa}, {v.tinh}
                        </TableCell>
                        <TableCell sx={AN_DUOI_MD}>{v.giongBo?.join(", ") || "—"}</TableCell>
                        <TableCell align="right">
                          <MDButton
                            variant="text"
                            color="info"
                            size="small"
                            onClick={() => {
                              setModalVuon(v);
                              setDiaChiVuonGiongChu(false);
                            }}
                          >
                            Sửa
                          </MDButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </MDBox>
            )}
          </Card>
        )}
      </MDBox>
      <Footer />

      {/* ---- Modal Chủ vườn (form dài → PC gần full khung, mobile fullScreen) ---- */}
      <Dialog
        open={!!modalChuVuon}
        onClose={() => setModalChuVuon(null)}
        fullWidth
        maxWidth="md"
        fullScreen={fullManHinh}
        PaperProps={oDialogPaperFormDai}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 2, flexShrink: 0, ...chuXuongDong }}>
          <NhanDau>{modalChuVuon?.id ? "Cập nhật" : "Thêm mới"}</NhanDau>
          <MDTypography variant="h5" component="h2">
            Chủ vườn
          </MDTypography>
        </DialogTitle>
        {modalChuVuon && (
          <MDBox component="form" onSubmit={luuChuVuon} sx={oDialogFormFlex}>
            <DialogContent sx={oDialogContentFormDai}>
              <Grid container spacing={2}>
                <FormChuVuon
                  value={modalChuVuon}
                  onChange={setModalChuVuon}
                  khoaCccd={!!modalChuVuon.id && !!soCccdDayDuTuHoSo(modalChuVuon)}
                />
              </Grid>
            </DialogContent>
            <DialogActions sx={oDialogActionsFormDai}>
              <MDButton variant="outlined" color="secondary" onClick={() => setModalChuVuon(null)}>
                Huỷ
              </MDButton>
              <MDButton type="submit" variant="gradient" color="info" disabled={dangLuu}>
                {dangLuu ? "Đang lưu..." : "Lưu"}
              </MDButton>
            </DialogActions>
          </MDBox>
        )}
      </Dialog>

      {/* ---- Modal Vườn (cùng kiểu form dài) ---- */}
      <Dialog
        open={!!modalVuon}
        onClose={() => setModalVuon(null)}
        fullWidth
        maxWidth="md"
        fullScreen={fullManHinh}
        PaperProps={oDialogPaperFormDai}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 2, flexShrink: 0, ...chuXuongDong }}>
          <NhanDau>{modalVuon?.id ? "Cập nhật" : "Thêm mới"}</NhanDau>
          <MDTypography variant="h5" component="h2">
            Vườn
          </MDTypography>
        </DialogTitle>
        {modalVuon && (
          <MDBox component="form" onSubmit={luuVuon} sx={oDialogFormFlex}>
            <DialogContent sx={oDialogContentFormDai}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MDInput
                    select
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    label="Chủ sở hữu *"
                    value={modalVuon.maChuVuon ?? ""}
                    onChange={(e) => chonChuSoHuu(e.target.value)}
                    disabled={!!modalVuon.id}
                    fullWidth
                    required
                  >
                    <option value="" disabled>
                      -- Chọn chủ vườn --
                    </option>
                    {chuVuonList.map((cv) => (
                      <option key={cv.id} value={cv.id}>
                        {cv.hoTen} — {hienThiCccd(cv, vaiTro) || "—"}
                      </option>
                    ))}
                  </MDInput>
                </Grid>
                <FormVuon
                  value={modalVuon}
                  onChange={setModalVuon}
                  chuVuon={chuVuonDangChon}
                  giongChuVuon={diaChiVuonGiongChu}
                  onChangeGiongChuVuon={setDiaChiVuonGiongChu}
                />
              </Grid>
            </DialogContent>
            <DialogActions sx={oDialogActionsFormDai}>
              <MDButton variant="outlined" color="secondary" onClick={() => setModalVuon(null)}>
                Huỷ
              </MDButton>
              <MDButton type="submit" variant="gradient" color="info" disabled={dangLuu}>
                {dangLuu ? "Đang lưu..." : "Lưu"}
              </MDButton>
            </DialogActions>
          </MDBox>
        )}
      </Dialog>

      <Dialog
        open={!!xemQrMaChuVuon}
        onClose={() => setXemQrMaChuVuon(null)}
        fullWidth
        maxWidth="xs"
        fullScreen={fullManHinh}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 2, ...chuXuongDong }}>
          <NhanDau>Mã QR</NhanDau>
          <MDTypography variant="h5" component="h2" sx={chuXuongDong}>
            {xemQrMaChuVuon?.hoTen}
          </MDTypography>
        </DialogTitle>
        <DialogContent sx={oDialogContent}>
          <MDTypography variant="caption" color="text" mb={2} display="block" sx={chuXuongDong}>
            In mã này để nhân viên thu mua quét/tra khi cần xác định nhanh chủ vườn.
          </MDTypography>
          {xemQrMaChuVuon && (
            <MDBox display="flex" flexDirection="column" alignItems="center">
              <QrCodeImage value={xemQrMaChuVuon.maChuVuon} />
              <TaiXuongQr
                value={xemQrMaChuVuon.maChuVuon}
                tenFile={`chu-vuon-${xemQrMaChuVuon.maChuVuon}`}
              />
              <MDBox mt={1.5} display="flex" flexWrap="wrap" justifyContent="center" gap={1}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={taiThePngMotCV}
                  disabled={dangXuLyHangLoat}
                >
                  {dangXuLyHangLoat ? "Đang tạo..." : "Tải thẻ PNG"}
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={taiThePdfMotCV}
                  disabled={dangXuLyHangLoat}
                >
                  {dangXuLyHangLoat ? "Đang tạo..." : "Tải thẻ PDF"}
                </MDButton>
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={oDialogActions}>
          <MDButton variant="outlined" color="secondary" onClick={() => setXemQrMaChuVuon(null)}>
            Đóng
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default QuanLyChuVuonVuon;
